import { useGameStore } from '@/stores/gameStore'
import { usePlanetStore } from '@/stores/planetStore'
import type PlayerPlanet from '@/types/PlayerPlanet'
import { useAssetStore } from '@/stores/assetStore'
import { useShipDesignStore } from '@/stores/shipDesignStore'
import type Ore from '@/types/Ore'
import type ProductionObject from '@/types/ProductionObject'
import type Player from '@/types/Player'
import type Building from '@/types/Building'
import type ShipComponent from '@/types/ShipComponent.ts'
import type PlayerShipDesign from '@/types/PlayerShipDesign.ts'
import type PlayerShip from '@/types/PlayerShip.ts'
import { useShipStore } from '@/stores/shipStore.ts'
import type Turn from '@/types/PublicRouter/Turn.ts'
import { useMapStore } from '@/stores/mapStore.ts'
import emitter from '@/models/eventBus.ts'
import { usePlayerRelationStore } from '@/stores/playerRelations.ts'
import type NewsItem from '@/types/NewsItem.ts'
import { useNewsStore } from '@/stores/newsStore.ts'
import type Tech from '@/types/Tech.ts'
import type PlanetQueueAutomation from '@/types/PlanetQueueAutomation.ts'
import { usePQAStore } from '@/stores/PQAStore.ts'
import { useFleetStore } from '@/stores/fleetStore.ts'
import type Fleet from '@/types/Fleet.ts'
import type PlayerRelation from '@/types/PlayerRelation.ts'
import type MapCoordinate from '@/types/MapCoordinate.ts'
import type StatKey from '@/types/StatKey.ts'
import { useStatsStore } from '@/stores/statsStore.ts'
import type ShipGroup from '@/types/ShipGroup.ts'
import { useShipGroupStore } from '@/stores/shipGroupStore.ts'
import type Ministry from '@/types/Ministry.ts'
import type PlanetGroup from '@/types/PlanetGroup.ts'
import { usePlanetGroupStore } from '@/stores/planetGroupStore.ts'
import { usePlayerOreStore } from '@/stores/playerOreStore.ts'
import { useOreConversionStore } from '@/stores/oreConversionStore.ts'
import type Ores from '@/types/Ores.ts'
import type PlayerOreTrade from '@/types/PlayerOreTrade.ts'
import { useSpyStore } from '@/stores/spyStore.ts'
import type PlayerSpy from '@/types/PlayerSpy.ts'
import type SpyMission from '@/types/SpyMission.ts'
import type FireBaseToken from '@/types/FireBaseToken.ts'
import type PlayerMinister from '@/types/PlayerMinister.ts'
import type Portal from '@/types/Portal.ts'
import { usePortalStore } from '@/stores/portalStore.ts'
import type ChatRoom from '@/types/ChatRoom.ts'
import type ChatMessage from '@/types/ChatMessage.ts'
import { useChatStore } from '@/stores/chatStore.ts'
import type ChatParticipant from '@/types/ChatParticipant.ts'
import type PlayerTechQueue from '@/types/TechQueue'
import { useAllianceStore } from '@/stores/allianceStore.ts'
import type Alliance from '@/types/Alliance.ts'
import { usePlayerFlagsStore } from '@/stores/playerFlagsStore.ts'
import { usePlayerColorStore } from '@/stores/playerColorStore.ts'
import { logger } from '@/models/logger.ts'

export default class PrivateRouter {
  private gameStore = useGameStore()
  private planetStore = usePlanetStore()
  private assetStore = useAssetStore()
  private shipDesignStore = useShipDesignStore()
  private shipStore = useShipStore()
  private shipGroupStore = useShipGroupStore()
  private planetGroupStore = usePlanetGroupStore()
  private fleetStore = useFleetStore()
  private mapStore = useMapStore()
  private playerRelationsStore = usePlayerRelationStore()
  private newsStore = useNewsStore()
  private pqaStore = usePQAStore()
  private statsStore = useStatsStore()
  private spyStore = useSpyStore()
  private playerOreStore = usePlayerOreStore()
  private oreConversionStore = useOreConversionStore()
  private chatStore = useChatStore()
  private portalStore = usePortalStore()
  private allianceStore = useAllianceStore()
  private playerFlagsStore = usePlayerFlagsStore()
  private playerColorStore = usePlayerColorStore()

  public route(event: string, data: Array<never>): void {
    // Only log non-frequent events to reduce noise
    if (event !== 'map_coordinate_update') {
      logger.debug('Routing private event: ' + event)
    }
    if (!this.gameStore.websocketInitialSyncCompleted) {
      this.gameStore.loadingText = event
    }
    switch (event) {
      case 'player_update':
        // Update Player Data
        this.updatePlayer(data)
        break
      case 'player_ore_update':
        this.playerOreUpdate(data)
        break
      case 'player_ship_designs_update':
        this.updatePlayerShipDesigns(data)
        break
      case 'player_ship_components_update':
        // Update Player Production Objects
        this.updatePlayerShipComponents(data)
        break
      case 'player_production_objects_update':
        // Update Player Production Objects
        this.updatePlayerProductionObjects(data)
        break
      case 'player_tech_update':
        // Update Player Owned Technologies
        this.updatePlayerTechs(data)
        break
      case 'planet_update':
        // Sync planet data
        this.planetUpdate(data)
        break
      case 'pqa_update':
        // Planet Queue Automation
        this.pqaUpdate(data)
        break
      case 'pqa_delete':
        // Planet Queue Automation
        this.pqaDelete(data)
        break
      case 'planet_delete':
        // Delete planet
        this.planetDelete(data)
        break
      case 'ship_update':
        // Sync Ship Data
        this.shipUpdate(data)
        break
      case 'ship_delete':
        // Delete Ship
        this.shipDelete(data)
        break
      case 'ship_group_update':
        // Sync Ship Group Data
        this.shipGroupUpdate(data)
        break
      case 'ship_group_delete':
        // Delete Ship Group
        this.shipGroupDelete(data)
        break
      case 'planet_group_update':
        // Sync Planet Group Data
        this.planetGroupUpdate(data)
        break
      case 'planet_group_delete':
        // Delete Planet Group
        this.planetGroupDelete(data)
        break
      case 'fleet_update':
        // Sync Fleet Data
        this.fleetUpdate(data)
        break
      case 'fleet_delete':
        // Delete Fleet
        this.fleetDelete(data)
        break
      case 'game_state_sync_completed':
        // Sync Initial Client Data
        this.gameStateSync()
        break
      case 'ore_assets':
        // Sync Ore Assets
        this.oreAssets(data)
        break
      case 'player_trade_update':
        // Update Player Trade Data
        this.updatePlayerTrade(data)
        break
      case 'production_object_assets':
        // Sync Production Object Assets
        this.productionObjectAssets(data)
        break
      case 'building_assets':
        // Sync Building Assets
        this.buildingAssets(data)
        break
      case 'tech_assets':
        // Sync Tech Assets
        this.techAssets(data)
        break
      case 'ship_component_assets':
        // Sync Ship Component Assets
        this.shipComponentAssets(data)
        break
      case 'turn':
        // Sync Initial Map Data
        this.turn(data)
        break
      case 'map_coordinate_update':
        this.mapCoordinateUpdate(data)
        break
      case 'player_relations_update':
        // Update Player Relations
        this.updatePlayerRelations(data)
        break
      case 'planet_type_assets':
        // Sync Planet Type Assets
        this.updatePlanetTypeAssets(data)
        break
      case 'star_type_assets':
        // Sync Star Type Assets
        this.updateStarTypeAssets(data)
        break
      case 'game_config':
        this.gameConfig(data)
        break
      case 'news_item':
        this.addPlayerNewsItems(data)
        break
      case 'stats_assets':
        this.addStatsAssets(data)
        break
      case 'ministry_assets':
        this.addMinistryAssets(data)
        break
      case 'player_ministers_update':
        this.addPlayerMinisters(data)
        break
      case 'spy_update':
        this.updateSpyUpdate(data)
        break
      case 'spy_mission_assets':
        this.updateSpyMissionAssets(data)
        break
      case 'fcm_device_update':
        this.updateFCMDevice(data)
        break
      case 'fcm_device_delete':
        this.deleteFcmDevice(data)
        break
      case 'fcm_news_type_update':
        this.updateFcmNewsType(data)
        break
      case 'portal_update':
        // Update Portal
        this.updatePortal(data)
        break
      case 'chat_room':
        this.updateChatRoom(data)
        break
      case 'chat_message':
        this.updateChatRoomMessage(data)
        break
      case 'chat_participant':
        this.updateChatRoomParticipant(data)
        break
      case 'chat_room_read':
        this.updateChatRoomReadStatus(data)
        break
      case 'chat_muted':
        this.updateChatMuted(data)
        break
      case 'request':
        this.processRequest(data)
        break
      case 'tech_queue_update':
        this.updateTechQueue(data)
        break
      case 'alliance_update':
        // Update Alliance
        this.updateAlliance(data)
        break
      case 'player_prototype_update':
        this.updatePlayerPrototype(data)
        break
      case 'leaderboard_update':
        // Update Leaderboard
        this.updateLeaderboard(data)
        break
      case 'player_flags_update':
        // Update Player Flags
        this.updatePlayerFlags(data)
        break
      case 'player_active_hours_update':
        // Update Player Active Hours
        this.updatePlayerActiveHours(data)
        break
      case 'player_building_settings_update':
        // Update Player Building Settings
        this.updatePlayerBuildingSettings(data)
        break
      case 'player_online_status_update':
        // Update Player Online Status
        this.updatePlayerOnlineStatus(data)
        break
      case 'player_color_labels_update':
        // Update Player Color Labels
        this.updatePlayerColorLabels(data)
        break
      default:
        logger.warn('Unknown event: ' + event)
        break
    }
  }

  private async updatePlayerPrototype(data: number): Promise<void> {
    this.gameStore.setPlayerPrototype(data)
  }

  private async updateTechQueue(data: PlayerTechQueue): Promise<void> {
    this.gameStore.setPlayerTechQueue(data)
  }

  private async updateChatRoomReadStatus(data: {
    chatRoomId: number
    lastMessageReadId: number
  }): Promise<void> {
    this.chatStore.setRoomLastMessageReadId(data.chatRoomId, data.lastMessageReadId)
  }

  private async updateChatMuted(data: { mutedPlayerIds: number[] }): Promise<void> {
    this.chatStore.setMutedPlayerIds(data.mutedPlayerIds)
  }

  public async updateChatRoom(data: ChatRoom): Promise<void> {
    logger.debug('updateChatRoom', data)
    this.chatStore.setRoom(data)
  }

  public async updateChatRoomParticipant(data: ChatParticipant): Promise<void> {
    this.chatStore.setParticipant(data)
  }

  public async updateChatRoomMessage(data: ChatMessage): Promise<void> {
    // Force message to be a string (defensive type coercion)
    data.message = String(data.message)
    this.chatStore.setMessage(data)
  }

  private async updatePortal(data: Portal): Promise<void> {
    this.portalStore.setPortal(data)
  }

  public async updateFCMDevice(data: FireBaseToken): Promise<void> {
    this.newsStore.addFirebaseToken(data)
  }

  public async deleteFcmDevice(data: { id: number }): Promise<void> {
    this.newsStore.deleteFirebaseToken(data.id)
  }

  public async updateFcmNewsType(data: { newsType: string; status: string }): Promise<void> {
    this.newsStore.updateNewsType(data.newsType, data.status)
  }

  private async updateSpyMissionAssets(data: SpyMission): Promise<void> {
    this.assetStore.updateSpyMissionByCode(data.code, data)
  }

  private async updateSpyUpdate(data: PlayerSpy): Promise<void> {
    this.spyStore.updateSpyById(data.id, data)
  }

  private async updatePlayerTrade(data: PlayerOreTrade): void {
    this.playerOreStore.setPlayerOreTrade(data)
  }

  private async addPlayerMinisters(minister: PlayerMinister[]): Promise<void> {
    this.gameStore.playerMinisters.set(minister.code, minister)
  }

  private async addMinistryAssets(data: Ministry): void {
    this.assetStore.updateMinistryByCode(data.code, data)
  }

  private async processRequest(data: Array<any>): void {
    this.gameStore.clearPendingRequest(data.requestId)
  }

  private async addStatsAssets(data: Array<StatKey>): void {
    for (const item of data) {
      this.statsStore.statsKeys.set(item.key, item)
    }
  }

  private async addPlayerNewsItems(data: NewsItem): void {
    this.newsStore.add(data)
    this.newsStore.addToast(data)
  }

  private async updatePlanetTypeAssets(data: Array<never>): void {
    this.assetStore.updatePlanetTypeById(data.id, data)
  }

  private async updateStarTypeAssets(data: Array<never>): void {
    this.assetStore.updateStarTypeById(data.id, data)
  }

  private async updatePlayerRelations(data: PlayerRelation): void {
    //data is an array of objects
    // console.log('TEST',data)
    this.playerRelationsStore.update(Number(data.playerId), data)
  }

  private async gameConfig(data: Array<never>): void {
    const eventData = data as unknown as {
      galaxyTileGridSize: number
      sectorTileGridSize: number
      dayInterval: number
      weekInterval: number
      ringworldOrePerPlanet: number
      combatMode: string
      gameFinished: string
    }

    this.gameStore.galaxyTileGridSize = eventData.galaxyTileGridSize
    this.gameStore.sectorTileGridSize = eventData.sectorTileGridSize
    this.gameStore.dayInterval = eventData.dayInterval
    this.gameStore.weekInterval = eventData.weekInterval
    this.gameStore.ringworldOrePerPlanet = eventData.ringworldOrePerPlanet
    this.gameStore.combatMode = eventData.combatMode
    this.gameStore.gameFinished = eventData.gameFinished === 'true'

    // Calculate the sectors
    this.mapStore.populateSectors()
  }

  private async mapCoordinateUpdate(data: MapCoordinate): void {
    emitter.emit('mapCoordinateUpdate', data)
  }

  private async turn(data: Array<never>): void {
    const eventData = data as unknown as Turn
    this.gameStore.turn = eventData.turn
    this.gameStore.dayTurnedAt = Date.now() / 1000 - eventData.dayOffset
    this.gameStore.weekTurnedAt = Date.now() / 1000 - eventData.weekOffset
    this.gameStore.startsIn = eventData.startsIn
    this.gameStore.startTime = new Date(Date.now() + eventData.startsIn * 1000)
  }

  /**
   * Sync Production Object Assets
   *
   * @param productionObject
   * @private
   */
  private async productionObjectAssets(productionObject: ProductionObject): void {
    this.assetStore.updateProductionObjectById(productionObject.id, productionObject)
  }

  /**
   * Sync Ore Assets
   *
   * @param ore
   * @private
   */
  private async oreAssets(ore: Ore): void {
    this.assetStore.updateOreById(ore.id, ore)
  }

  /**
   * Sync Building Assets
   *
   * @param building
   * @private
   */
  private async buildingAssets(building: Building): void {
    this.assetStore.updateBuildingById(building.buildingCode, building)
  }

  /**
   * Sync Tech Assets
   *
   * @param tech
   * @private
   */
  private async techAssets(tech: Tech): void {
    this.assetStore.updateTechByCode(tech.id, tech)
  }

  /**
   * Sync Ship Component Assets
   */
  private async shipComponentAssets(shipComponent: ShipComponent): void {
    this.assetStore.updateShipComponentById(shipComponent.code, shipComponent)
  }

  /**
   * Sync Game State for the client
   *
   * @private
   */
  private async gameStateSync(): void {
    // We are now in sync
    this.gameStore.websocketInitialSyncCompleted = true
    logger.info('Game State Synced')
  }

  /**
   * Update Player Data
   * @param data
   * @private
   */
  private async updatePlayer(data: unknown): void {
    this.gameStore.player = data as unknown as Player
  }

  /**
   * Update Player Ship Designs
   *
   * @param data
   * @private
   */
  private async updatePlayerShipDesigns(data: PlayerShipDesign): void {
    this.shipDesignStore.playerShipDesigns.set(data.id, data)
  }

  /**
   * Update Player Ore Data
   *
   * @param data
   * @private
   */
  private async playerOreUpdate(data: { ores: Ores; conversionData: {} }): void {
    // Update Player Ore Data
    for (const [key, value] of Object.entries(data.ores)) {
      this.playerOreStore.setValue(key as keyof typeof this.playerOreStore.data, value as number)
    }

    // Update Ore Conversion Data
    for (const [key, value] of Object.entries(data.conversionData)) {
      this.oreConversionStore.setValue(
        key as keyof typeof this.oreConversionStore.data,
        value as number
      )
    }
  }

  /**
   * Update Player Production Objects
   *
   * @param data
   * @private
   */
  private async updatePlayerShipComponents(data: Any): void {
    this.gameStore.playerShipComponents.set(data.id, data.id)
  }

  /**
   * Update Player Production Objects
   *
   * @param data
   * @private
   */
  private async updatePlayerProductionObjects(data: any): void {
    this.gameStore.playerProductionObjects.set(data.id, data.id)
  }

  /**
   * Update Player Owned Technologies
   *
   * @param data
   * @private
   */
  private async updatePlayerTechs(data: Array<never>): void {
    data.forEach((eventData: string) => {
      this.gameStore.playerTechs.set(eventData)
    })
  }

  /**
   * Sync Planet Data
   *
   * @param data
   * @private
   */
  private async planetUpdate(data: PlayerPlanet): void {
    this.planetStore.updatePlanetById(data.id, data)
  }

  /**
   * Planet Queue Automation
   *
   * @param data
   * @private
   */
  private async pqaUpdate(data: PlanetQueueAutomation): void {
    this.pqaStore.updateQueue(data)
  }

  private async pqaDelete(data: { pqaId: number }): void {
    this.pqaStore.deleteQueue(data.pqaId)
  }

  /**
   * Delete Planet
   *
   * @param data
   * @private
   */
  private async planetDelete(data: Array<never>): void {
    const eventData = data as unknown as {
      planetId: number
    }
    this.planetStore.deletePlanetById(eventData.planetId)
  }

  /**
   * Sync Ship Data
   *
   * @param data
   * @private
   */
  private async shipUpdate(data: PlayerShip): void {
    this.shipStore.updateShipById(data.id, data)
  }

  /**
   * Add/Update Ship Group
   *
   * @param data
   * @private
   */
  private async shipGroupUpdate(data: ShipGroup): void {
    this.shipGroupStore.updateShipGroupById(data.id, data)
  }

  /**
   * Delete Ship Group
   */
  private async shipGroupDelete(data: Array<never>): void {
    const eventData = data as unknown as {
      shipGroupId: number
    }
    this.shipGroupStore.deleteShipGroupById(eventData.shipGroupId)
  }

  /**
   * Add/Update Ship Group
   *
   * @param data
   * @private
   */
  private async planetGroupUpdate(data: PlanetGroup): void {
    this.planetGroupStore.updatePlanetGroupById(data.id, data)
  }

  /**
   * Delete Ship Group
   */
  private async planetGroupDelete(data: Array<never>): void {
    const eventData = data as unknown as {
      planetGroupId: number
    }
    this.planetGroupStore.deletePlanetGroupById(eventData.planetGroupId)
  }

  /**
   * Sync Fleet Data
   *
   * @param data
   * @private
   */
  private async fleetUpdate(data: Fleet): void {
    this.fleetStore.updateFleetById(data.id, data)
  }

  /**
   * Delete Fleet
   *
   * @param data
   * @private
   */
  private async fleetDelete(data: Array<never>): void {
    const eventData = data as unknown as {
      fleetId: number
    }
    this.fleetStore.deleteFleetById(eventData.fleetId)
  }

  /**
   * Delete Ship
   *
   * @param data
   * @private
   */
  private async shipDelete(data: Array<never>): void {
    const eventData = data as unknown as {
      shipId: number
    }
    this.shipStore.deleteShipById(eventData.shipId)
  }

  /**
   * Update Alliance
   *
   * @param data
   * @private
   */
  private async updateAlliance(data: Alliance): Promise<void> {
    // Force the name to be a string
    data.name = String(data.name)
    this.allianceStore.updateAlliance(data)
  }

  /**
   * Delete Alliance
   *
   * @param data
   * @private
   */
  private async allianceDelete(data: { allianceId: number }): Promise<void> {
    this.allianceStore.removeAlliance(data.allianceId)
  }

  /**
   * Update Leaderboard
   *
   * @param data
   * @private
   */
  private async updateLeaderboard(data: any): Promise<void> {
    logger.debug('updateLeaderboard', data)
    this.gameStore.leaderboard = data
  }

  /**
   * Update Player Flags
   *
   * @param data
   * @private
   */
  private async updatePlayerFlags(data: Record<string, string>): Promise<void> {
    this.playerFlagsStore.updateFlags(data)
  }

  /**
   * Update Player Active Hours
   *
   * @param data
   * @private
   */
  private async updatePlayerActiveHours(data: { hours_online_24h: number; hours_online_limit: number; active_timestamps: number[] }): Promise<void> {
    this.gameStore.hoursOnline = data.hours_online_24h
    this.gameStore.hoursOnlineLimit = data.hours_online_limit
    this.gameStore.activeTimestamps = data.active_timestamps || []
  }

  /**
   * Update Player Building Settings
   *
   * @param data
   * @private
   */
  private async updatePlayerBuildingSettings(data: Record<string, number>): Promise<void> {
    // Clear existing settings
    this.gameStore.playerBuildingSettings.clear()
    // Populate Map from received data
    for (const [key, value] of Object.entries(data)) {
      this.gameStore.playerBuildingSettings.set(key, value)
    }
  }

  /**
   * Update Player Online Status
   *
   * @param data
   * @private
   */
  private async updatePlayerOnlineStatus(data: Array<never>): Promise<void> {
    const eventData = data as unknown as { onlinePlayers: Record<string, number>; serverTimestamp: number }
    this.playerRelationsStore.updateOnlineStatus(eventData.onlinePlayers, eventData.serverTimestamp)
  }

  /**
   * Update Player Color Labels
   *
   * @param data Object with playerId (as string keys) -> hex color (e.g., { "123": "#FF0000" })
   * @private
   */
  private async updatePlayerColorLabels(data: Array<never>): Promise<void> {
    const eventData = data as unknown as Record<string, string>
    this.playerColorStore.updatePlayerColors(eventData)
  }
}
